import { defineStore } from 'pinia'
import * as inspectionApi from '../services/api/inspection'

export interface IInspectionData {
  scheduled_at: string
  car_id: number | null
  inspection_manager_id: number | null
  assignee_ids: Array<number>
  note: string
  approvals: Array<any>
  car: any
  id: number
  status: string
  _assessor_employees: string
}

export const useInspectionStore = defineStore('inspection', {
  state: () => ({
    inspectionCommand: {
      id: 0,
      scheduled_at: '',
      car_id: null,
      inspection_manager_id: null,
      assignee_ids: [],
      approvals: [],
      car: null,
      status: '',
      note: '',
      _assessor_employees: '',
    } as IInspectionData,
    isLoading: false,
  }),

  actions: {
    setInspection(data: IInspectionData) {
      this.inspectionCommand = data
    },

    setStatus(data: string) {
      this.inspectionCommand.status = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getInspection(id: number) {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.get(id)
        this.setInspection(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateInspection(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.update(this.inspectionCommand.id, payload)
        this.setInspection(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectInspection(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.reject(this.inspectionCommand.id, payload)
        this.setInspection(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveInspection() {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.approve(this.inspectionCommand.id, {})
        this.setInspection(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyInspection(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.apply(this.inspectionCommand.id, payload)
        this.setInspection(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeInspection(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.remake(this.inspectionCommand.id, payload)
        this.setInspection(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async processInspection(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.process(this.inspectionCommand.id, payload)
        this.setInspection(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createInspection(payload: any) {
      try {
        this.setLoading(true)
        const result = await inspectionApi.create(payload)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useInspectionListStore = defineStore('inspectionList', {
  state: () => ({
    inspections: [] as IInspectionData[],
    inspectionsByCar: [] as IInspectionData[],
    count: 0,
    isLoading: false,
    total: 0,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    setInspections(data: IInspectionData[]) {
      this.inspections = data
    },

    setInspectionsByCar(data: IInspectionData[]) {
      this.inspectionsByCar = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    setTotal(value: number) {
      this.total = value
    },

    async countInspections() {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listInspections(params: any) {
      try {
        this.setLoading(true)
        const result = await inspectionApi.list(params)
        const { data, total } = result as any
        this.setInspections(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllInspections(params: any) {
      try {
        this.setLoading(true)
        const result = await inspectionApi.listAll(params)
        const { data, total } = result as any
        this.setInspections(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listInspectionsByCar(car_id: number) {
      try {
        this.setLoading(true)
        const { data } = await inspectionApi.listAll({ car_id })
        this.setInspectionsByCar(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
